import React, { useState } from "react";
import {
  FileCode, FileText, Zap, Workflow, Database, Layout, Archive,
  XCircle, AlertCircle, Loader2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogMain, DialogTitle, DialogDescription } from "@ncoder/ui";
import { useOrg } from "@/hooks/org/useOrg";
import { useMetadataDeployments } from "@/hooks/deployment";
import { DeploymentStatus, type MetadataDeployment } from "@/services/deployment/types";

const getFileIcon = (type: string) => {
  switch (type) {
    case "ApexClass":       return <FileCode className="h-3.5 w-3.5 text-blue-400" />;
    case "ApexTrigger":     return <Zap className="h-3.5 w-3.5 text-purple-400" />;
    case "LightningComponentBundle":
    case "AuraDefinitionBundle": return <Layout className="h-3.5 w-3.5 text-cyan-400" />;
    case "Flow":            return <Workflow className="h-3.5 w-3.5 text-green-400" />;
    case "CustomObject":    return <Database className="h-3.5 w-3.5 text-orange-400" />;
    case "ApexPage":        return <FileText className="h-3.5 w-3.5 text-indigo-400" />;
    case "StaticResource":  return <Archive className="h-3.5 w-3.5 text-zinc-400" />;
    default:                return <FileCode className="h-3.5 w-3.5 text-zinc-400" />;
  }
};

function formatTime(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export const FailedDeployments: React.FC = () => {
  const [selected, setSelected] = useState<MetadataDeployment | null>(null);

  const { selectedOrg } = useOrg();
  const orgUuid = selectedOrg?.id ?? "";

  const { data: deployments, isLoading, error } = useMetadataDeployments(
    orgUuid,
    DeploymentStatus.FAILED,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs">Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 px-4 text-center">
        <AlertCircle className="h-5 w-5 text-destructive" />
        <p className="text-xs text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  if (!deployments?.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 px-4 text-center">
        <XCircle className="h-6 w-6 text-muted-foreground/40" />
        <p className="text-xs text-muted-foreground">No failed deployments</p>
      </div>
    );
  }

  return (
    <>
      <div className="divide-y divide-border/50">
        {deployments.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setSelected(d)}
            className="w-full flex flex-col gap-1 px-5 py-3 text-left hover:bg-muted/40 transition-colors"
          >
            <div className="flex items-center gap-3">
              <XCircle className="h-3.5 w-3.5 text-rose-400 shrink-0" />

              <div className="flex-1 min-w-0 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4">
                <span className="text-xs font-mono text-foreground truncate">
                  {d.id.slice(0, 8)}…
                </span>
                <span className="text-xs text-muted-foreground">
                  {d.metadataItems?.length ?? 0} file{(d.metadataItems?.length ?? 0) !== 1 ? "s" : ""}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatTime(d.startedAt)}
                </span>
              </div>

              {d.insertedAt && (
                <span className="text-[10px] text-muted-foreground/60 shrink-0">
                  {new Date(d.insertedAt).toLocaleDateString()}
                </span>
              )}
            </div>

            {d.errorMessage && (
              <p className="ml-6 text-[11px] text-rose-400/80 truncate">{d.errorMessage}</p>
            )}
          </button>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="sm:max-w-[780px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-500/10">
                <XCircle className="h-4 w-4 text-rose-400" />
              </div>
              <div>
                <div className="text-sm font-semibold">Deployment Failure Details</div>
                {selected && (
                  <div className="text-xs font-normal text-muted-foreground mt-0.5">
                    {selected.metadataItems?.length ?? 0} file(s) · {selected.id}
                  </div>
                )}
              </div>
            </DialogTitle>
            <DialogDescription>Files included in this failed deployment</DialogDescription>
          </DialogHeader>

          {selected && (
            <DialogMain className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Status", value: <span className="text-rose-400 font-medium">{selected.status}</span> },
                  { label: "Files", value: `${selected.metadataItems?.length ?? 0}` },
                  { label: "Started", value: formatTime(selected.startedAt) },
                  { label: "Failed at", value: formatTime(selected.completedAt) },
                ].map(({ label, value }) => (
                  <div key={label} className="px-3 py-2.5 rounded-lg bg-muted/30 border border-border/60">
                    <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wide">{label}</p>
                    <p className="text-xs text-foreground">{value}</p>
                  </div>
                ))}
              </div>

              {selected.errorMessage && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-rose-500/8 border border-rose-500/20">
                  <AlertCircle className="h-3.5 w-3.5 text-rose-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-rose-400 leading-relaxed">{selected.errorMessage}</p>
                </div>
              )}

              <div className="rounded-lg border border-border overflow-hidden">
                <div className="bg-muted/40 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground sticky top-0">
                  Files ({selected.metadataItems?.length ?? 0})
                </div>
                <div className="divide-y divide-border/50 max-h-64 overflow-y-auto">
                  {selected.metadataItems?.map((item) => (
                    <div key={item.id} className="flex items-center gap-2.5 px-3 py-2">
                      {getFileIcon(item.metadataType)}
                      <span className="text-xs text-foreground truncate flex-1">{item.fullName}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{item.metadataType}</span>
                    </div>
                  ))}
                </div>
              </div>
            </DialogMain>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
